import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  MethodNode,
  ProcedureDataParameterPlannedValue,
  ProcedureParameterDataTypeDefinition,
} from 'procedures/ProcedureDomainTypes';
import {FieldDefinitionGraphQL} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNodeSectionData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {fieldTypeToFieldDefinitionType} from 'schemas/fields/FieldDefinitionCompatibilityUtils';
import {
  FieldType,
  isRequiredLinkFolderItemType,
  isRequiredLinkTagSchema,
  RequiredLink,
  requiredLinkToSchemaId,
} from 'schemas/fields/SchemaFieldConstants';
import {assertNever} from 'util/Types';

export interface ProcedureFieldInfo {
  cid: string;
  name: string;
  dataType: ProcedureParameterDataTypeDefinition;
  // Setpoints for data parameters
  values?: Array<ProcedureDataParameterPlannedValue>;
  // Stable identifier across all versions of a parameter. Null until parameter is persisted.
  procedureParameterId: string | null;
}

/**
 * Field group keys used to categorize procedure fields.
 * Keep in sync with ProcedureFieldGroupKey in
 * benchling/procedures/controller/processes_platform/procedure_method_task_execution_type_adapter.py
 */
export enum ProcedureFieldGroupKey {
  MATERIAL_INPUTS = 'MATERIAL_INPUTS',
  MATERIAL_OUTPUTS = 'MATERIAL_OUTPUTS',
  DATA_PARAMETERS = 'DATA_PARAMETERS',
  EQUIPMENT = 'EQUIPMENT',
}

/**
 * Returns a stable field client identifier for a procedure parameter.
 *
 * Uses the procedure parameter ID (stable across renames) when available.
 * Returns an identifier that is unique within the field group as a fallback.
 * This fallback identifier is safe to use because for Procedures application, we don't rely on storing the
 * field identifiers within the processes flowchart system, our parameters are stored in our PMDV application data.
 * So this fallback identifier is only used transiently in the UI until we receive the updated fields with their
 * procedure parameter IDs. See ProcedureMethodNodeAdapter:updateTaskDefinitionVersionWithApplicationData to
 * understand how we replace the input and output field definitions within the PDTV with each change made to the flowchart.
 *
 * Keep in sync with the backend:
 * ParameterSettingToWorkflowFieldDefinitionMapper.resolve_field_client_identifier
 */
export function getFieldClientId(field: ProcedureFieldInfo, fieldGroupKey: ProcedureFieldGroupKey): string {
  if (field.procedureParameterId != null) {
    return field.procedureParameterId;
  }
  return `${fieldGroupKey}/${field.name}`;
}

function requiredLinkToSchemaConstraintId(requiredLink: RequiredLink | null): string | null {
  const schemaId = requiredLinkToSchemaId(requiredLink);
  if (schemaId != null) {
    return schemaId;
  }

  if (isRequiredLinkFolderItemType(requiredLink)) {
    return `folderItemType:${requiredLink.folderItemType}`;
  }

  return null;
}

/**
 * Maps a TagSchemaSummary's folderItemType to a representative TagSchemaItemType.
 *
 * This is used when constructing frontend field definitions for validation. The validation code
 * (resolveTypeFromSchemaLink) needs a TagSchemaItemType to determine the FieldDefinitionType.
 * Multiple TagSchemaItemTypes within a folderItemType category all resolve to the same
 * FieldDefinitionType, so any representative value is correct for validation purposes.
 */
function folderItemTypeToTagSchemaItemType(folderItemType: string): TagSchemaItemType {
  switch (folderItemType) {
    case ModelTypeConstants.SEQUENCE:
      return TagSchemaItemType.DNA_SEQUENCE;
    case ModelTypeConstants.PROTEIN:
      return TagSchemaItemType.AA_SEQUENCE;
    case ModelTypeConstants.MIXTURE:
      return TagSchemaItemType.MIXTURE;
    case ModelTypeConstants.MOLECULE:
      return TagSchemaItemType.MOLECULE;
    case ModelTypeConstants.ENTRY:
      return TagSchemaItemType.ENTRY;
    case ModelTypeConstants.BASIC_FOLDER_ITEM:
    default:
      return TagSchemaItemType.BASIC_FOLDER_ITEM;
  }
}

/**
 * Converts a procedure field (procedure field format) to a field definition (PTDV format).
 *
 * Keep in sync with backend: `benchling/domain_graph/mappers/platform/field_definition_type_resolver.py`
 */
export function createFieldDefinitionGraphQL(
  field: ProcedureFieldInfo,
  fieldGroupKey: ProcedureFieldGroupKey
): FieldDefinitionGraphQL {
  const fieldClientIdentifier = getFieldClientId(field, fieldGroupKey);
  const fieldType = field.dataType.type;
  const fieldDefinitionType = fieldTypeToFieldDefinitionType(fieldType);

  const baseFieldDefinition = {
    id: field.cid,
    name: field.name,
    // Procedure fields are always single-value and not required
    isMulti: false,
    isRequired: false,
    type: fieldDefinitionType,
    // Procedure fields are never archived
    archiveData: {
      __typename: 'ArchiveDataNode' as const,
      archived: false,
    },
  };

  const fieldDefinition =
    fieldType === FieldType.BIOENTITY_LINK
      ? {
          __typename: 'SchemaLinkFieldDefinitionNode' as const,
          ...baseFieldDefinition,
          schema: (() => {
            const requiredLink = field.dataType.requiredLink;
            // When the material links to a specific tag schema, produce a TagSchemaNode so
            // that resolveTypeFromSchemaLink can extract itemType for proper type validation.
            // Without this, the frontend-generated field definitions use a generic __typename
            // that causes type resolution to fall back to ENTITY_LINK, while backend-generated
            // field definitions correctly resolve to specific types (e.g., DNA_SEQUENCE_LINK),
            // leading to spurious "mismatched types" errors on material edges.
            if (isRequiredLinkTagSchema(requiredLink)) {
              const tagSchema = requiredLink.tagSchema;
              return {
                __typename: 'TagSchemaNode' as const,
                id: tagSchema.id,
                itemType: folderItemTypeToTagSchemaItemType(tagSchema.folderItemType),
              };
            }
            const schemaId = requiredLinkToSchemaConstraintId(requiredLink);
            return schemaId != null
              ? ({__typename: 'ProcessTaskSchemaNode' as const, id: schemaId} as const)
              : null;
          })(),
          interface: null,
          folderItemType: null,
        }
      : {
          __typename: 'BaseFieldDefinitionNode' as const,
          ...baseFieldDefinition,
        };

  return {
    __typename: 'ProcessFieldDefinitionNode' as const,
    fieldIdentifier: fieldClientIdentifier,
    fieldClientIdentifier,
    fieldGroupKey,
    fieldDefinition,
  };
}

/**
 * Gets fields for a specific procedure field group from the task definition version.
 * Material outputs use outputFieldDefinitions, all others use inputFieldDefinitions.
 */
export function getFieldsForGroup(
  methodDefinitionVersion: MethodNode,
  fieldGroupKey: ProcedureFieldGroupKey
): ReadonlyArray<ProcedureFieldInfo> {
  switch (fieldGroupKey) {
    case ProcedureFieldGroupKey.MATERIAL_OUTPUTS:
      return methodDefinitionVersion.materialOutputSettings.map(({materialOutputVersion}) => ({
        ...materialOutputVersion,
        procedureParameterId: materialOutputVersion.procedureMaterialOutputId,
      }));
    case ProcedureFieldGroupKey.MATERIAL_INPUTS:
      return methodDefinitionVersion.materialInputSettings.map(({materialInputVersion}) => ({
        ...materialInputVersion,
        procedureParameterId: materialInputVersion.procedureMaterialInputId,
      }));
    case ProcedureFieldGroupKey.DATA_PARAMETERS:
      return methodDefinitionVersion.dataParameterSettings.map(({dataParameterVersion, values}) => ({
        ...dataParameterVersion,
        values,
        procedureParameterId: dataParameterVersion.procedureDataParameterId,
      }));
    case ProcedureFieldGroupKey.EQUIPMENT:
      return methodDefinitionVersion.equipmentSettings.map(({equipmentVersion}) => ({
        ...equipmentVersion,
        procedureParameterId: equipmentVersion.procedureEquipmentId,
      }));
    default:
      assert(false, `Unknown field group key: ${assertNever(fieldGroupKey)}`);
      return [];
  }
}

/**
 * Creates input and output field definitions from a method node's application data.
 */
export function createFieldDefinitionsFromMethodNode(applicationData: MethodNode): {
  inputFieldDefinitions: Array<FieldDefinitionGraphQL>;
  outputFieldDefinitions: Array<FieldDefinitionGraphQL>;
} {
  const inputFieldDefinitions: Array<FieldDefinitionGraphQL> = getFieldsForGroup(
    applicationData,
    ProcedureFieldGroupKey.MATERIAL_INPUTS
  ).map((field) => createFieldDefinitionGraphQL(field, ProcedureFieldGroupKey.MATERIAL_INPUTS));

  const outputFieldDefinitions: Array<FieldDefinitionGraphQL> = getFieldsForGroup(
    applicationData,
    ProcedureFieldGroupKey.MATERIAL_OUTPUTS
  ).map((field) => createFieldDefinitionGraphQL(field, ProcedureFieldGroupKey.MATERIAL_OUTPUTS));

  return {inputFieldDefinitions, outputFieldDefinitions};
}

/**
 * Updates the clientIds of section rows to match current procedure parameter IDs.
 *
 * After a save, procedure parameter IDs get assigned by the server. The sections
 * (which are used as ReactFlow handle sources) need their clientIds updated so that
 * edges can correctly connect to the right handles.
 */
export function updateSectionClientIds(
  sections: Array<TaskSchemaNodeSectionData>,
  applicationData: MethodNode
): Array<TaskSchemaNodeSectionData> {
  const fieldGroupKeys = Object.values(ProcedureFieldGroupKey);
  return sections.map((section, sectionIndex) => {
    const fieldGroupKey = fieldGroupKeys[sectionIndex];
    if (fieldGroupKey == null) {
      return section;
    }
    const methodFields = getFieldsForGroup(applicationData, fieldGroupKey);
    return {
      ...section,
      sectionRows: section.sectionRows.map((row, rowIndex) => {
        const field = methodFields[rowIndex];
        if (field == null) {
          return row;
        }
        return {
          ...row,
          clientId: getFieldClientId(field, fieldGroupKey),
        };
      }),
    };
  });
}
